import { put, call, all, select } from 'redux-saga/effects';
import { push } from 'redux-first-history';
import { createQadraSaga } from './shared';
import {
  apiModelPortfoliosGet,
  apiModelPortfoliosHoldingsGet,
  apiModelPortfoliosKpisGet,
  apiModelPortfolioPerformancesGet,
  apiModelPortfoliosPost,
  apiModelPortfoliosHoldingsPatch,
  apiModelPortfoliosDelete,
  apiModelPortfoliosDetailsGet,
  apiModelPortfoliosMaxDrawdownGet,
} from '../api/modelPortfolios';
import {
  MODEL_PORTFOLIOS_GET,
  MODEL_PORTFOLIOS_DELETE,
  MODEL_PORTFOLIOS_HOLDINGS_GET,
  MODEL_PORTFOLIOS_KPIS_GET,
  MODEL_PORTFOLIOS_PERFORMANCES_GET,
  MODEL_PORTFOLIOS_WITH_KPIS_GET,
  MODEL_PORTFOLIOS_POST,
  MODEL_PORTFOLIOS_HOLDINGS_PATCH,
  MODEL_PORTFOLIOS_DETAILS_GET,
  MODEL_PORTFOLIOS_MAX_DRAWDOWN_GET,
} from '../actions/types';
import { faPaths, withId } from '../../paths';
import { getServerErrorMessage } from '../../utils';
import { actionBadRequestErrorSet, actionBadRequestErrorClear } from '../actions';

function* modelPortfoliosGet({ payload: { userId, riskProfileId } }) {
  const { modelPortfolios } = yield call(apiModelPortfoliosGet, userId, { riskProfileId });
  yield put({
    type: MODEL_PORTFOLIOS_GET._SUCCESS,
    payload: { modelPortfolios, riskProfileId },
  });
}

const watchModelPortfoliosGet = createQadraSaga(modelPortfoliosGet, {
  action: MODEL_PORTFOLIOS_GET._REQUEST,
});

function* modelPortfoliosPost({ payload: { userId, name, riskProfileId, category, serviceTypeId } }) {
  const { modelPortfolio } = yield call(apiModelPortfoliosPost, userId, { name, riskProfileId, category, serviceTypeId });
  yield put(push(withId(faPaths.modelPortfolios.details, [modelPortfolio.id, 'modelPortfolioId'])));
}

const watchModelPortfoliosPost = createQadraSaga(modelPortfoliosPost, {
  action: MODEL_PORTFOLIOS_POST._REQUEST,
});

function* modelPortfoliosWithKpisGet({ payload: { userId } }) {
  const { modelPortfolios } = yield call(apiModelPortfoliosGet, userId);

  const kpisCalls = modelPortfolios.map((m) => call(apiModelPortfoliosKpisGet, m.id));
  const modelPortfoliosKpis = yield all(kpisCalls);

  yield put({
    type: MODEL_PORTFOLIOS_WITH_KPIS_GET._SUCCESS,
    payload: { modelPortfolios, modelPortfoliosKpis },
  });
}

const watchModelPortfoliosWithKpisGet = createQadraSaga(modelPortfoliosWithKpisGet, {
  action: MODEL_PORTFOLIOS_WITH_KPIS_GET._REQUEST,
});

function* modelPortfoliosDelete({ payload: { userId, modelPortfolioIds } }) {
  const calls = modelPortfolioIds.map((id) => call(apiModelPortfoliosDelete, id));
  yield all(calls);

  yield modelPortfoliosWithKpisGet({ payload: { userId } });
}

const watchModelPortfoliosDelete = createQadraSaga(modelPortfoliosDelete, {
  action: MODEL_PORTFOLIOS_DELETE._REQUEST,
});

function* modelPortfolioHoldingsGet({ payload: { modelPortfolioId } }) {
  const { proposalHoldings } = yield call(apiModelPortfoliosHoldingsGet, modelPortfolioId);
  yield put({
    type: MODEL_PORTFOLIOS_HOLDINGS_GET._SUCCESS,
    payload: { modelPortfolioId, holdings: proposalHoldings },
  });
}

const watchModelPortfolioHoldingsGet = createQadraSaga(modelPortfolioHoldingsGet, {
  action: MODEL_PORTFOLIOS_HOLDINGS_GET._REQUEST,
});

function* modelPortfolioDetailsGet({ payload: { modelPortfolioId } }) {
  const { modelPortfolio } = yield call(apiModelPortfoliosDetailsGet, modelPortfolioId);
  yield put({
    type: MODEL_PORTFOLIOS_DETAILS_GET._SUCCESS,
    payload: { modelPortfolioId, modelPortfolio },
  });
}

const watchModelPortfolioDetailsGet = createQadraSaga(modelPortfolioDetailsGet, {
  action: MODEL_PORTFOLIOS_DETAILS_GET._REQUEST,
});

function* modelPortfolioKpisGet({ payload: { modelPortfolioId } }) {
  const { kpis } = yield call(apiModelPortfoliosKpisGet, modelPortfolioId);
  yield put({
    type: MODEL_PORTFOLIOS_KPIS_GET._SUCCESS,
    payload: { modelPortfolioId, kpis },
  });
}

const watchModelPortfolioKpisGet = createQadraSaga(modelPortfolioKpisGet, {
  action: MODEL_PORTFOLIOS_KPIS_GET._REQUEST,
});

function* modelPortfolioPerformancesGet({
  payload: { modelPortfolioId, benchmarkId, yearsTag, compareIds },
}) {
  const { chart } = yield call(apiModelPortfolioPerformancesGet, modelPortfolioId, {
    benchmarkId,
    yearsTag,
    compareIds,
  });

  yield put({
    type: MODEL_PORTFOLIOS_PERFORMANCES_GET._SUCCESS,
    payload: {
      modelPortfolioId,
      performanceChartBenchmarkId: benchmarkId,
      performanceChart: chart,
      compareIds,
      performanceChartYearsTag: yearsTag,
    },
  });
}

const watchModelPortfolioPerformanceGet = createQadraSaga(modelPortfolioPerformancesGet, {
  action: MODEL_PORTFOLIOS_PERFORMANCES_GET._REQUEST,
});

function* modelPortfoliosHoldingsPatch({ payload: { modelPortfolioId, holdings } }) {
  const performance = yield select((s) => s.modelPortfolios.performances[modelPortfolioId]);

  yield call(apiModelPortfoliosHoldingsPatch, modelPortfolioId, { holdings });

  const updates = [
    modelPortfolioHoldingsGet({ payload: { modelPortfolioId } }),
    modelPortfolioKpisGet({ payload: { modelPortfolioId } }),
    modelPortfolioDetailsGet({ payload: { modelPortfolioId } }),
  ];

  if (performance) {
    updates.push(
      modelPortfolioPerformancesGet({
        payload: {
          modelPortfolioId,
          benchmarkId: performance.performanceChartBenchmarkId,
          yearsTag: performance.performanceChartYearsTag,
        },
      })
    );
  }

  yield all(updates);
}

const watchModelPortfoliosHoldingsPatch = createQadraSaga(modelPortfoliosHoldingsPatch, {
  action: MODEL_PORTFOLIOS_HOLDINGS_PATCH._REQUEST,
  *onBadRequestError(e) {
    const message = getServerErrorMessage(e);
    yield put(actionBadRequestErrorSet(message, MODEL_PORTFOLIOS_HOLDINGS_PATCH._ERROR));
  },
  *onBegin() {
    yield put(actionBadRequestErrorClear(MODEL_PORTFOLIOS_HOLDINGS_PATCH._ERROR));
  },
});

export function* modelPortfoliosMaxDrawdownGet({
  payload: { modelPortfolioId, startDate, endDate, benchmarkId, samplingInterval },
}) {
  const { maxDrawdown } = yield call(apiModelPortfoliosMaxDrawdownGet, {
    modelPortfolioId,
    startDate,
    endDate,
    benchmarkId,
    samplingInterval,
  });

  yield put({
    type: MODEL_PORTFOLIOS_MAX_DRAWDOWN_GET._SUCCESS,
    payload: { maxDrawdown, modelPortfolioId },
  });
}

const watchModelPortfolioMaxDrawdownGet = createQadraSaga(modelPortfoliosMaxDrawdownGet, {
  action: MODEL_PORTFOLIOS_MAX_DRAWDOWN_GET._REQUEST,
  *onBadRequestError(e) {
    const message = getServerErrorMessage(e);
    yield put(actionBadRequestErrorSet(message, MODEL_PORTFOLIOS_MAX_DRAWDOWN_GET._ERROR));
  },
  *onBegin() {
    yield put(actionBadRequestErrorClear(MODEL_PORTFOLIOS_MAX_DRAWDOWN_GET._ERROR));
  },
});

export default [
  watchModelPortfoliosGet,
  watchModelPortfoliosPost,
  watchModelPortfoliosDelete,
  watchModelPortfoliosWithKpisGet,
  watchModelPortfolioDetailsGet,
  watchModelPortfolioHoldingsGet,
  watchModelPortfoliosHoldingsPatch,
  watchModelPortfolioKpisGet,
  watchModelPortfolioPerformanceGet,
  watchModelPortfolioMaxDrawdownGet,
];
